/** The item master and the two group masters its code is built from.
 *
 * Kept out of registry.ts because these three carry more explanation than
 * the rest put together — the code scheme is FKMT's own, recovered from the
 * old system, and the reasons matter more than the field list.
 *
 * ITEM_MASTER.md is the specification.
 */

import type { MasterSpec } from '@/types'

export const ITEM_GROUPS: MasterSpec = {
  module: 'masters', object: 'item_group', title: 'Item Groups',
  singular: 'Item Group',
  path: 'masters/item-groups', idKey: 'group_code',
  searchPlaceholder: 'Search groups…',
  columns: [
    { key: 'group_code', label: 'Code', width: '100px' },
    { key: 'name', label: 'Name' },
  ],
  fields: [
    { key: 'group_code', label: 'Code', required: true, immutable: true,
      help: 'Two digits — the first two of every item code in this group' },
    { key: 'name', label: 'Name', required: true },
  ],
}

/** The four prefixes are four fields because they are four different things:
 *  the same sub-group prefixes a plain item differently from an assembly.
 *  Leaving one blank means this sub-group cannot hold that category at all —
 *  RAW MATERIAL is an item but never an assembly — and the system refuses
 *  rather than inventing a code. */
export const ITEM_SUBGROUPS: MasterSpec = {
  module: 'masters', object: 'item_subgroup', title: 'Item Sub-Groups',
  singular: 'Item Sub-Group',
  path: 'masters/item-subgroups', idKey: 'subgroup_code',
  idGenerated: true,
  idGeneratedHelp: 'Issued by the system — the meaningful code is the Item Prefix below',
  searchPlaceholder: 'Search sub-groups…',
  // No Code column — the system issues it and it carries no meaning. The
  // codes that DO matter here are the four prefixes.
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'classification', label: 'Class', width: '80px' },
    { key: 'prefix_item', label: 'Item', width: '70px' },
    { key: 'prefix_subassembly', label: 'Sub-Assy', width: '90px' },
    { key: 'prefix_assembly', label: 'Assembly', width: '90px' },
    { key: 'prefix_main_assembly', label: 'Main Assy', width: '95px' },
  ],
  fields: [
    { key: 'name', label: 'Name', required: true },
    { key: 'classification', label: 'Classification',
      help: 'FKMT’s own hierarchy — 1.1 FRAME, 2.1 HYDRAULIC' },
    { key: 'prefix_item', label: 'Item Prefix',
      help: 'Two digits. Blank means this sub-group holds no plain items' },
    { key: 'prefix_subassembly', label: 'Sub-Assembly Prefix',
      help: 'Four digits, or blank' },
    { key: 'prefix_assembly', label: 'Assembly Prefix',
      help: 'Four digits, or blank' },
    { key: 'prefix_main_assembly', label: 'Main Assembly Prefix',
      help: 'Four digits, or blank' },
  ],
}

/** The item master.
 *
 * The code is GENERATED — nine digits, group + sub-group + serial — so the
 * form states what will happen rather than offering a box the server ignores.
 *
 * No stock balance, no average rate and no last purchase rate appear here.
 * All three are derived from the movement ledger, per item per location. A
 * rate cached on the item master is exactly how GHERP's valuation drifted:
 * its rptStkBal was written from 136 separate places.
 */
export const ITEMS: MasterSpec = {
  module: 'masters', object: 'item', title: 'Items',
  singular: 'Item',
  path: 'masters/items', idKey: 'item_id',
  idGenerated: true,
  idGeneratedHelp: 'Generated on save, from the group, sub-group and category',
  searchPlaceholder: 'Search items by code, name or HSN…',
  // The only spec with a panel. Two linked grids for the product families an
  // assembly is used on — ITEM_MASTER.md §10, and FKMT's own screen.
  panel: 'families',
  columns: [
    { key: 'item_id', label: 'Code', width: '110px' },
    { key: 'item_name', label: 'Name' },
    { key: 'item_category', label: 'Category', width: '120px' },
    { key: 'item_type', label: 'Type', width: '120px' },
    { key: 'uom', label: 'UOM', width: '80px' },
    { key: 'hsn_code', label: 'HSN', width: '100px' },
    { key: 'standard_cost', label: 'Standard Cost', width: '130px',
      numeric: true, value: true },
  ],
  fields: [
    { key: 'item_name', label: 'Name', required: true },
    { key: 'description', label: 'Description', type: 'textarea',
      help: 'Specification, grade, drawing reference' },
    { key: 'item_category', label: 'Category', type: 'select', required: true,
      options: [
        { value: 'Item', label: 'Item' },
        { value: 'SubAssembly', label: 'Sub-Assembly' },
        { value: 'Assembly', label: 'Assembly' },
        { value: 'MainAssembly', label: 'Main Assembly' },
      ],
      help: 'Decides which prefix the code is built from' },
    { key: 'group_code', label: 'Group',
      optionsFrom: { path: 'masters/item-groups', value: 'group_code', label: 'name' },
      help: 'Required for a plain item; assemblies do not use it' },
    { key: 'subgroup_code', label: 'Sub-Group', required: true,
      optionsFrom: { path: 'masters/item-subgroups', value: 'subgroup_code', label: 'name' } },
    { key: 'uom', label: 'Unit of Measure', required: true,
      optionsFrom: { path: 'masters/uom', value: 'uom_code', label: 'name' },
      help: 'One unit per item — bought and consumed in the same one' },
    { key: 'item_type', label: 'Type', type: 'select', required: true,
      options: [
        { value: 'RawMaterial', label: 'Raw Material' },
        { value: 'BoughtOut', label: 'Bought-out' },
        { value: 'Consumable', label: 'Consumable' },
        { value: 'Tool', label: 'Tool' },
        { value: 'FinishedGoods', label: 'Finished Goods' },
        { value: 'Spare', label: 'Spare' },
      ] },
    { key: 'make_or_buy', label: 'Make or Buy', type: 'select', required: true,
      options: [
        { value: 'Make', label: 'Make' },
        { value: 'Buy', label: 'Buy' },
        /* No "Both" — ITEM_MASTER.md §5.1. A different choice on one
           project is an override recorded on that project. */
      ],
      /* GHERP's rule, enforced since migration 045: a plain item has no
         bill of material, so there is nothing to make it from. */
      help: 'A plain Item is always Buy — anything made is a sub-assembly or above' },
    /* Agreed 8 Sep 2026, built 11 Sep (migration 048). Weight lets a BOM
       total in kilograms and the plate-cutting balance work; size carries
       the dimensions a cutting list is read by. BOM.md §3.4, §7. */
    { key: 'net_weight', label: 'Weight per Unit (kg)', type: 'number',
      help: 'Not needed for an item stocked in KG — its weight is its quantity' },
    { key: 'item_size', label: 'Size / Dimensions',
      help: 'As printed on a cutting list — e.g. 10 x 1500 x 6000' },
    { key: 'article_no', label: 'Article Number',
      help: 'A customer’s or supplier’s own part number, if they use one' },
    { key: 'article_desc', label: 'Article Description' },
    { key: 'item_make', label: 'Make',
      help: 'Brand — matters on a bought-out bearing, cylinder or fitting' },
    { key: 'hsn_code', label: 'HSN Code', help: 'Exactly 8 digits' },
    /* No GST rate here. It is resolved from the HSN code and the document
       date (migration 038) — a rate per item is four thousand rows to
       change when GST moves, and four thousand chances to miss one. */
    /* Restored 7 Sep 2026. In GHERP this is read in ~85 files as
       `NonStkable <> 'Y'` — it keeps services, freight and AMC lines out of
       every stock screen and report. Nobody types into it, which is exactly
       why it was the field nearest to being dropped. */
    { key: 'non_stockable', label: 'Stockable', type: 'select',
      options: [
        { value: 'false', label: 'Yes — this is stock' },
        { value: 'true', label: 'No — a service or expense, never stocked' },
      ],
      help: 'A non-stockable item can be bought and costed, but never reaches the stock ledger' },
    /* Restored 7 Sep 2026 after being dropped the same day. The GHERP flag
       was enforced nowhere — but the missing half was never the flag, it was
       the STATE. A QA-required item is received into INSPECTION and cannot be
       issued until somebody releases it. Migration 036. */
    { key: 'qa_required', label: 'Inspection on Receipt', type: 'select',
      options: [
        { value: 'false', label: 'No — goes straight to free stock' },
        { value: 'true', label: 'Yes — held for inspection before it can be issued' },
      ],
      help: 'Received into INSPECTION. Nobody can issue it until it is released' },
    { key: 'valuation_class', label: 'Valuation', type: 'select',
      options: [
        { value: 'Valued', label: 'Valued' },
        { value: 'Free', label: 'Free of cost' },
      ],
      help: 'Free material raises quantity but never value' },
    { key: 'needs_route', label: 'Has Operations', type: 'select',
      options: [
        { value: 'false', label: 'No — no route needed' },
        { value: 'true', label: 'Yes — this item goes through operations' },
      ],
      help: 'Tick this and the item appears on the Item Routes screen' },
    /* Reorder — ledger D25. GHERP captured all three of these, validated the
       quantity, and used none of them: not one occurrence in its 72 views or
       31 stored procedures. Here they drive the reorder suggestion list.

       Recommended for C-class items only. An A-class item is planned from the
       project and its BOM, where demand is known, and a reorder level on top
       of that would double-count it. */
    { key: 'reorder_managed', label: 'Reorder by Level', type: 'select',
      options: [
        { value: 'false', label: 'No — planned from the project BOM' },
        { value: 'true', label: 'Yes — order when it falls below level' },
      ],
      help: 'Best for C-class items: fasteners, consumables, fittings' },
    { key: 'reorder_level', label: 'Reorder Level', type: 'number' },
    { key: 'reorder_qty', label: 'Reorder Quantity', type: 'number',
      help: 'How much to order when it drops below the level' },
    { key: 'lead_time_days', label: 'Lead Time (days)', type: 'number' },
    { key: 'standard_cost', label: 'Standard Cost', type: 'number', value: true,
      help: 'A figure you decide — not the average, which the ledger derives' },
  ],
}

/** The product line: MT/SL (was THP/SL), FABRICATION, SPARES.
 *
 * GHERP's `MstPrjF`. The code is generated and hidden — `PrjFID` there is a
 * row number, and nobody has ever said "family 3".
 *
 * `OHCOST` is not carried. GHERP held an overhead cost per family; FKMT's
 * answer on 7 Sep 2026 was that the data behind it was never collected, and
 * the rate is now set annually with the CA — STOCK_AND_COSTING.md §7.3.
 */
export const PRODUCT_FAMILIES: MasterSpec = {
  module: 'masters', object: 'family', title: 'Product Families',
  singular: 'Product Family',
  path: 'masters/product-families', idKey: 'family_code',
  idGenerated: true,
  searchPlaceholder: 'Search families…',
  columns: [
    { key: 'name', label: 'Family' },
    { key: 'description', label: 'Description' },
  ],
  fields: [
    { key: 'name', label: 'Family Name', required: true,
      help: 'The product line — MT/SL, FABRICATION, SPARES' },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
}

/** The machine within a family: 9M Deck, MT/SL 4, MT/SL 6, MT/SL Accessories.
 *
 * GHERP's `MstMM`, with `MstMMFamily` folded into a column because a model
 * belongs to exactly one family.
 *
 * This is the level a BOM is actually built at, which is why FKMT made the
 * model compulsory on an item's tag — ITEM_MASTER.md §10.3.
 */
export const PRODUCT_MODELS: MasterSpec = {
  module: 'masters', object: 'family', title: 'Equipment Models',
  singular: 'Equipment Model',
  path: 'masters/product-models', idKey: 'model_code',
  idGenerated: true,
  searchPlaceholder: 'Search models…',
  // No Family column: the master dispatcher selects this table's own columns
  // and does not join, so a family NAME is not available here — and the
  // family CODE is generated and meaningless. The family is a filter instead.
  columns: [
    { key: 'name', label: 'Model' },
    { key: 'description', label: 'Description' },
  ],
  fields: [
    { key: 'family_code', label: 'Family', required: true,
      optionsFrom: { path: 'masters/product-families',
                     value: 'family_code', label: 'name' },
      help: 'A model belongs to exactly one family' },
    { key: 'name', label: 'Model Name', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
}

/** GST rates, by HSN code and effective date.
 *
 * NOT a rate per item. With four thousand items that would be four thousand
 * rows to change when a rate moves, and four thousand chances to miss one.
 * GHERP had this right — `MstTaxDtls` keys the rate on the HSN — and the first
 * cut of our item master did not, so `gst_rate` came off the item.
 *
 * A change is a NEW ROW with a later effective date, never an edit: an invoice
 * raised last year must still price the way it priced. Migration 038, ledger
 * D30.
 */
export const TAX_RATES: MasterSpec = {
  module: 'masters', object: 'tax_rate', title: 'GST Rates',
  singular: 'GST Rate',
  path: 'masters/tax-rates', idKey: 'hsn_code',
  hasStatus: false,
  searchPlaceholder: 'Search by HSN code or description…',
  columns: [
    { key: 'hsn_code', label: 'HSN', width: '110px' },
    { key: 'description', label: 'Description' },
    { key: 'effective_from', label: 'Effective From', width: '130px' },
    { key: 'cgst_percent', label: 'CGST %', width: '90px', numeric: true },
    { key: 'sgst_percent', label: 'SGST %', width: '90px', numeric: true },
    { key: 'igst_percent', label: 'IGST %', width: '90px', numeric: true },
  ],
  fields: [
    { key: 'hsn_code', label: 'HSN Code', required: true, immutable: true,
      help: '4 to 8 digits — the classification, not the item' },
    { key: 'effective_from', label: 'Effective From', type: 'date',
      required: true, immutable: true,
      help: 'A rate change is a NEW row from the date it applies — the old one stays, because invoices were raised under it' },
    { key: 'description', label: 'Description',
      help: 'What this code covers, in your words' },
    { key: 'cgst_percent', label: 'CGST %', type: 'number', required: true },
    { key: 'sgst_percent', label: 'SGST %', type: 'number', required: true },
    { key: 'igst_percent', label: 'IGST %', type: 'number', required: true,
      help: 'Must equal CGST + SGST — the database enforces it' },
    { key: 'cess_percent', label: 'Cess %', type: 'number' },
  ],
}
